import { useTranslation } from 'react-i18next'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import { formatShortDateWithTimeSeconds } from '@dhl/utils'
import { EasySearchHitDto, EasySearchOverviewDto } from '@dhl/mdp-api'
import { Body1, Box, Paper, Tooltip } from '@dhl-ui/components'
import { ClipboardCopy } from '~/modules/ppp/components'
import { AdminDataGrid } from '~/common'
import {
  overviewHasErrorGenerator,
  overviewSomeHaveFilenames,
  maybeOverviewWarningOrErrorIcon,
} from '~/modules/ppp/helpers'

type Props = {
  overview: EasySearchOverviewDto[]
  hits: EasySearchHitDto[]
  onSelectRow: (correlationId: string) => void
  correlationId: string
  onlyErrors: boolean
}
export const PppOverview = ({ overview, hits, onSelectRow, correlationId, onlyErrors }: Props) => {
  const { t } = useTranslation()
  const overviewHasError = overviewHasErrorGenerator(hits)
  const indexedOverview = overview.map((o) => ({ ...o, id: o.correlationId }))
  const overviewToShow = indexedOverview.filter((o) => !onlyErrors || overviewHasError(o))
  const tooltipTitle = (value: string) => (!value || value === '-' ? t('admin.unknown') : value)
  const renderCell = ({ value }: GridRenderCellParams) => (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Body1>
        <ClipboardCopy payload={value} size={14} />
        <Tooltip title={tooltipTitle(value)}>
          <span>{value}</span>
        </Tooltip>
      </Body1>
    </Box>
  )

  const columns: GridColDef<EasySearchOverviewDto>[] = [
    {
      field: 'error',
      headerName: '',
      renderCell: ({ row }) =>
        overviewHasError(row) ? (
          <Tooltip title={t('admin.pppPage.overview.error')}>{maybeOverviewWarningOrErrorIcon(hits)(row)}</Tooltip>
        ) : null,
      width: 24,
    },
    {
      field: 'trackingCode',
      headerName: t('admin.pppPage.overview.trackingCode'),
      width: 250,
      renderCell,
    },
    {
      field: 'fileName',
      headerName: t('admin.pppPage.overview.fileName'),
      width: overviewSomeHaveFilenames(overview) ? 200 : 80,
      hideable: true,
      valueGetter: (value) => value ?? t('admin.pppPage.overview.unknownFile'),
      renderCell,
    },
    {
      field: 'inputFormat',
      headerName: t('admin.pppPage.overview.inputFormat'),
      width: 100,
      renderCell,
    },
    {
      field: 'outputFormat',
      headerName: t('admin.pppPage.overview.outputFormat'),
      width: 120,
      renderCell,
    },
    { field: 'flowName', headerName: t('admin.pppPage.overview.flowName'), renderCell, width: 150 },
    {
      field: 'flowType',
      headerName: t('admin.pppPage.overview.flowType'),
      width: 150,
      renderCell,
    },
    {
      field: 'addedTimestamp',
      headerName: t('admin.pppPage.overview.addedTimestamp'),
      width: 200,
      valueGetter: (value) => formatShortDateWithTimeSeconds(value),
      renderCell,
    },
    {
      field: 'correlationId',
      headerName: t('admin.pppPage.overview.correlationId'),
      width: 350,
      hideable: true,
      renderCell,
    },
  ]
  return (
    <Paper>
      <AdminDataGrid
        hideFooter
        disableRowSelectionOnClick
        paginationModel={{ pageSize: overview.length, page: 0 }} // absolutely enforce disabling pagination all the time
        columns={columns}
        rows={overviewToShow}
        onRowClick={({ id }) => {
          onSelectRow(id as string)
        }}
        getRowClassName={({ id }) => {
          return id === correlationId ? '--ppp-selected-row' : ''
        }}
        sx={({ palette: { grey } }) => ({
          '& .MuiDataGrid-row': { backgroundColor: 'transparent !important' },
          '& .--ppp-selected-row': { backgroundColor: `${grey[300]} !important` },
        })}
      />
    </Paper>
  )
}
